import React from 'react';
import { View, Text, TouchableOpacity, ActivityIndicator } from 'react-native';

import { useAppTheme } from '../../../design-system/ThemeProvider';
import { createAuthStyles } from '../AuthScreen.styles';

interface EmailConfirmationMessageProps {
  email: string;
  onResend: () => void;
  onBackToLogin: () => void;
  loading: boolean;
  error: string;
}

const EmailConfirmationMessage: React.FC<EmailConfirmationMessageProps> = ({
  email,
  onResend,
  onBackToLogin,
  loading,
  error
}) => {
  const { colors } = useAppTheme();
  const styles = createAuthStyles(colors);
  return (
    <View style={{ width: '100%', padding: 8 }}>
      {/* Success Icon */}
      <View style={{ alignItems: 'center', marginBottom: 24 }}>
        <View style={{
          width: 80,
          height: 80,
          borderRadius: 40,
          backgroundColor: 'rgba(16, 185, 129, 0.12)',
          justifyContent: 'center',
          alignItems: 'center',
          marginBottom: 16
        }}>
          <Text style={{ fontSize: 40 }}>📧</Text>
        </View>
        <Text style={{
          fontSize: 24,
          fontWeight: 'bold',
          color: '#1D1A23',
          textAlign: 'center',
          marginBottom: 8
        }}>
          Check Your Email
        </Text>
      </View>

      {/* Instructions */}
      <View style={{
        backgroundColor: '#F5F3FA',
        borderRadius: 12,
        padding: 20,
        marginBottom: 24
      }}>
        <Text style={{
          fontSize: 16,
          color: '#464555',
          textAlign: 'center',
          marginBottom: 16,
          lineHeight: 24
        }}>
          We've sent a confirmation link to:
        </Text>

        <Text style={{
          fontSize: 16,
          fontWeight: 'bold',
          color: '#4F46E5',
          textAlign: 'center',
          marginBottom: 20
        }}>
          {email}
        </Text>

        <View style={{
          backgroundColor: '#E0E7FF',
          borderLeftWidth: 3,
          borderLeftColor: '#4F46E5',
          padding: 12,
          borderRadius: 8
        }}>
          <Text style={{
            fontSize: 14,
            color: '#464555',
            lineHeight: 20
          }}>
            📌 Click the link in the email to activate your account
            {'\n\n'}
            💡 Didn't receive it? Check your spam folder
            {'\n\n'}
            ⏰ The link expires in 24 hours
          </Text>
        </View>
      </View>

      {/* Error Message */}
      {error && error !== "Email sent! Check your inbox." ? (
        <Text style={{
          color: '#EF4444',
          marginBottom: 16,
          textAlign: 'center',
          fontSize: 14
        }}>
          {error}
        </Text>
      ) : null}

      {/* Success Message for Resend */}
      {error === "Email sent! Check your inbox." ? (
        <Text style={{
          color: '#10B981',
          marginBottom: 16,
          textAlign: 'center',
          fontSize: 14,
          fontWeight: '600'
        }}>
          ✅ {error}
        </Text>
      ) : null}

      {/* Resend Button */}
      <TouchableOpacity
        onPress={onResend}
        disabled={loading}
        style={{
          backgroundColor: '#FFFFFF',
          borderRadius: 12,
          padding: 16,
          marginBottom: 16,
          borderWidth: 1,
          borderColor: '#C7C4D8'
        }}
      >
        <View style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center' }}>
          {loading ? (
            <ActivityIndicator color="#4F46E5" style={{ marginRight: 8 }} />
          ) : (
            <Text style={{ fontSize: 18, marginRight: 8 }}>🔄</Text>
          )}
          <Text style={{
            color: '#1D1A23',
            fontSize: 16,
            fontWeight: '600'
          }}>
            Resend Confirmation Email
          </Text>
        </View>
      </TouchableOpacity>

      {/* Back to Login Button */}
      <TouchableOpacity
        onPress={onBackToLogin}
        disabled={loading}
        style={{
          padding: 16,
          alignItems: 'center'
        }}
      >
        <Text style={{
          color: '#4F46E5',
          fontSize: 16,
          fontWeight: '600',
          textDecorationLine: 'underline'
        }}>
          ← Back to Login
        </Text>
      </TouchableOpacity>

      {/* Additional Help */}
      <View style={{
        marginTop: 16,
        padding: 16,
        backgroundColor: '#E0E7FF',
        borderRadius: 8,
        borderWidth: 1,
        borderColor: '#C7C4D8'
      }}>
        <Text style={{
          fontSize: 13,
          color: '#464555',
          textAlign: 'center',
          lineHeight: 18
        }}>
          💬 Need help? Once you confirm your email, you'll be able to sign in and start your Kingdom Bible Quiz journey!
        </Text>
      </View>
    </View>
  );
};

export default EmailConfirmationMessage;
